import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { asyncStoragePersister } from './lib/query-persistence.js';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PwaUpdaterProvider } from './contexts/PwaUpdaterContext.jsx';
import { applyBootstrapQueryDefaults } from './lib/app-bootstrap.js';
import { initAnalytics, trackEvent } from './lib/app-telemetry.js';
import AppErrorBoundary from './components/AppErrorBoundary.jsx';
import App from './App.jsx';
import { applyBranding } from './lib/branding.js';

const showQueryDevtools = import.meta.env.DEV && import.meta.env.VITE_SHOW_QUERY_DEVTOOLS === 'true';
const PRELOAD_RELOAD_KEY = 'vite-preload-reload-attempted';
const PRELOAD_RELOAD_COOLDOWN_MS = 30 * 1000;
const BOOT_WATCHDOG_SHOWN_KEY = 'blade_boot_watchdog_shown';
const QUERY_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24;

try {
  const bootWatchdogShownAt = window.localStorage.getItem(BOOT_WATCHDOG_SHOWN_KEY);
  if (bootWatchdogShownAt) {
    window.localStorage.removeItem(BOOT_WATCHDOG_SHOWN_KEY);
    trackEvent('app_boot_watchdog_recovered', {
      shown_at: bootWatchdogShownAt,
      recovered_at: Date.now(),
      path: `${window.location.pathname}${window.location.search}`,
      user_agent: navigator.userAgent,
    });
  }
} catch {
  // Ignore storage errors in restricted browser modes.
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  trackEvent('app_preload_error', {
    message: event.payload?.message || String(event.payload || ''),
    stack: event.payload?.stack || null,
    path: `${window.location.pathname}${window.location.search}`,
    user_agent: navigator.userAgent,
  });

  const lastReloadAt = Number(window.sessionStorage.getItem(PRELOAD_RELOAD_KEY) || 0);
  if (Date.now() - lastReloadAt < PRELOAD_RELOAD_COOLDOWN_MS) {
    return;
  }

  window.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1,   // 1 min — no refetch on remount during normal nav
      gcTime: 1000 * 60 * 60 * 24, // 24 hours — keep in memory/cache
      retry: 1,
      refetchOnWindowFocus: false, // 600 ms RTT makes this annoying
    },
  },
});

applyBootstrapQueryDefaults(queryClient);

function isEmptyValue(value) {
  return value === undefined || value === null || value === '';
}

function isAllowedCustomerListKey(params = {}) {
  return (
    params.page === 1 &&
    (params.limit === 5 || params.limit === 10) &&
    isEmptyValue(params.search) &&
    (params.sortBy === undefined || params.sortBy === 'createdAt') &&
    (params.sortDirection === undefined || params.sortDirection === 'desc')
  );
}

function isAllowedSalesListKey(params = {}) {
  return (
    params.page === 1 &&
    params.limit === 10 &&
    params.timeFilter === 'day' &&
    isEmptyValue(params.staffId) &&
    isEmptyValue(params.paymentMethodId) &&
    isEmptyValue(params.saleType) &&
    isEmptyValue(params.from) &&
    isEmptyValue(params.to)
  );
}

function isAllowedTopItemsKey(queryKey = []) {
  const [
    resource,
    type,
    userId,
    startDate,
    endDate,
    itemType,
    limit,
    employeeId,
  ] = queryKey;

  return (
    resource === 'metrics' &&
    type === 'top-items' &&
    Boolean(userId) &&
    typeof startDate === 'string' &&
    typeof endDate === 'string' &&
    itemType === 'all' &&
    limit === 8 &&
    employeeId === 'all'
  );
}

function shouldPersistQuery(query) {
  if (!defaultShouldDehydrateQuery(query)) return false;

  const [resource, type, params] = query.queryKey;

  if (resource === 'products' || resource === 'services' || resource === 'payment-methods') {
    return true;
  }

  if (resource === 'customers' && type === 'list') {
    return isAllowedCustomerListKey(params);
  }

  if (resource === 'sales' && type === 'list') {
    return isAllowedSalesListKey(params);
  }

  if (resource === 'metrics' && type === 'top-items') {
    return isAllowedTopItemsKey(query.queryKey);
  }

  return false;
}

applyBranding();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        maxAge: QUERY_CACHE_MAX_AGE_MS, // 24 hours
        buster: import.meta.env.VITE_APP_VERSION || "v1",
        dehydrateOptions: {
          shouldDehydrateQuery: shouldPersistQuery,
        },
      }}
    >
      <AppErrorBoundary>
        <PwaUpdaterProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PwaUpdaterProvider>
      </AppErrorBoundary>
      {showQueryDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </PersistQueryClientProvider>
  </React.StrictMode>
)

initAnalytics();
